import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Trans, t } from '@lingui/macro'
import { Box, Typography, Slider, Alert, Button, Stack } from '@mui/material'

import TextInputController from '@/components/ui/inputs/TextInputController'

import { useConsultationSettingsForm } from '../model/consultationSettingsForm'
import { useGetConsultationSettingsQuery } from '@/core/services/consultation'

export function ConsultationSettings() {
  const { data: settings } = useGetConsultationSettingsQuery()

  const initialValues = useMemo(() => ({ automaticCloseHours: settings?.automaticCloseHours ?? 2 }), [settings])

  const { isSubmitting, rhForm, submitHandler } = useConsultationSettingsForm({ initialValues })
  const {
    formState: { isDirty },
  } = rhForm

  const automaticCloseHours = rhForm.watch('automaticCloseHours')

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        <Trans>Automatic consultation deletion</Trans>
      </Typography>
      <Typography variant="body1" gutterBottom>
        <Trans>Inactive consultations are automatically deleted after the configured period of inactivity.</Trans>
      </Typography>

      <Box sx={{ mt: 3 }}>
        <form onSubmit={rhForm.handleSubmit(submitHandler)}>
          <Typography gutterBottom>
            <Trans>Automatically delete consultations after {automaticCloseHours} hours of inactivity</Trans>
          </Typography>
          <Controller
            control={rhForm.control}
            name="automaticCloseHours"
            render={({ field: { value } }) => (
              <Slider
                value={value}
                onChange={(_event, newValue) => rhForm.setValue('automaticCloseHours', Array.isArray(newValue) ? newValue[0] : newValue)}
                min={1}
                max={8}
                step={1}
                marks={[
                  { value: 1, label: '1h' },
                  { value: 2, label: '2h' },
                  { value: 4, label: '4h' },
                  { value: 8, label: '8h' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => t`${value}h`}
              />
            )}
          />
          <TextInputController
            control={rhForm.control}
            fullWidth
            inputProps={{ min: 1, max: 8, step: 1 }}
            label={t`Hours of inactivity`}
            name="automaticCloseHours"
            sx={{ mt: 2 }}
            type="number"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Trans>Move the slider or enter a value, then click Update to change the automatic deletion delay.</Trans>
          </Alert>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" disabled={!isDirty} type="submit">
              {isSubmitting ? t`Saving...` : t`Update`}
            </Button>
          </Stack>
        </form>
      </Box>
    </Box>
  )
}
